<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount, type PropType } from 'vue'
import { useI18n } from '../composables/useI18n'
import ModeIcon from './ModeIcon.vue'
import { walkCapabilities } from '../walkthrough/walkState'
import ModeBadge from './ModeBadge.vue'
import { MODE_NAME_KEY, modeNameKey, type Mode } from './modes'
import { isBuildMode } from '../script/modeName'
import { myGroupCount } from '../composables/useStudyGroups'
import { RELEASE_HOLDS } from '../config/releaseHolds'
import {
  offlineDlState,
  offlineDownloadPct,
  offlineDownloadVisible,
} from '../composables/useOfflineDownloadStatus'

const { t } = useI18n()

const props = defineProps({
  isListeningMode: { type: Boolean, default: false },
  isPronunciationMode: { type: Boolean, default: false },
  // showListeningBtn / showPronunciationBtn are the localStorage-backed
  // visibility flags (ssi-mode-listening, ssi-mode-pronunciation) that Settings
  // and the between-rounds mode tips write. They deliberately do NOT gate the
  // rows below — the listening row has been ungated since before the tray
  // existed in this shape, and gating it on a flag that defaults to false would
  // hide the control from every learner who has never opened developer
  // settings, which is exactly the thing Tom asked for on 2026-08-06.
  showListeningBtn: { type: Boolean, default: false },
  showPronunciationBtn: { type: Boolean, default: false },
  // The course in play. Kept on the tray's contract for callers that pass it;
  // the selector itself does not need it.
  courseCode: { type: String, default: '' },
  // SCRIPT mode (job #453): the written form of sounds the learner already
  // owns. Shown only for courses whose target script we teach (Arabic first).
  hasScriptPractice: { type: Boolean, default: false },
  isScriptPracticeMode: { type: Boolean, default: false },
  // Signed in, not a guest, not a school account (job #113): gates the Talk to
  // humans and Study groups rows. Named for Connect (job #488), its first user.
  showConnect: { type: Boolean, default: false },
  // Off the player (RoutedBottomNav) the page says which mode is lit: 'talk',
  // 'studyGroups', or '' for a learner page that is none of them. null = on
  // the player, where the mode is read from the flags above.
  pageMode: { type: String as PropType<string | null>, default: null },
  isVisible: { type: Boolean, default: true },
})

const emit = defineEmits([
  'speaking', 'toggleListening', 'toggleScriptPractice', 'talk', 'studyGroups'
])

const isOpen = ref(false)

const toggleTray = () => {
  isOpen.value = !isOpen.value
}

// ── Offline-download ring around the mode button ────────────────────────────
// Offline is switched on from Settings now (2026-09-24), but a download runs
// for minutes while the learner is in the player, so its progress stays here,
// on the button they can always see. The ring is
// a conic-gradient on a child of the button itself (anchored via inset to the
// button's own box → can NEVER drift off-centre, unlike an overlaid sibling SVG).
// Drive it with CSS vars: --dl-pct fills the green arc; --dl-color sets green
// (downloading/complete) or red (error). complete/error show a full ring.
const offlineRingVars = computed(() => {
  let pct = 0
  let color = '#16a34a'
  if (offlineDlState.value === 'complete' || offlineDlState.value === 'partial') pct = 100  // partial = READY (green)
  else if (offlineDlState.value === 'error') { pct = 100; color = '#ef4444' }
  else if (offlineDlState.value === 'locked') { pct = 100; color = '#f59e0b' }  // amber: lease paused
  else if (offlineDownloadPct.value !== null) pct = offlineDownloadPct.value
  else if (offlineDlState.value === 'preparing') pct = 12   // small green arc + pulse (no total yet)
  return { '--dl-pct': pct, '--dl-color': color }
})

const closeTray = () => {
  isOpen.value = false
}

// ── Tap-anywhere-outside dismiss ────────────────────────────────────────────
// This is done at the DOCUMENT level, not by the backdrop element, and that is
// deliberate. The backdrop below is `position: fixed; inset: 0`, which sounds
// full-screen but is not: .bottom-nav (this tray's ancestor) carries
// `transform: translateX(-50%)`, and a transformed ancestor becomes the
// containing block for fixed descendants — so the "full-screen" backdrop only
// ever covered the ~400x60px nav pill. A tap anywhere else on the screen hit
// nothing and the tray stayed open, which is the bug Tom reported repeatedly.
// The 2026-07-16 fix (1c41b1d2) could not fix this either way round: teleporting
// the backdrop to <body> escapes the embedded containing block and paints OVER
// the tray, eating every row tap. A document listener sidesteps the whole
// containing-block/stacking question — it works in embedded and standalone play
// alike, and cannot swallow a tap meant for the tray or its trigger.
const rootEl = ref<HTMLElement | null>(null)

// After an outside pointerdown dismisses the tray, swallow the click that same
// press is about to produce, so dismissing never doubles as a tap on whatever
// was underneath (mid-session that would be the transport — pausing the audio
// by accident). Standard overlay semantics: first tap outside just dismisses.
// Scoped to the element that press landed on: if no click follows (the press
// was a scroll or a drag) the arming must not sit there and eat someone's next,
// unrelated tap — reopening the tray included.
let swallowTimer: ReturnType<typeof setTimeout> | null = null
let swallowTarget: EventTarget | null = null
const swallowNextClick = (e: MouseEvent) => {
  const samepress = swallowTarget instanceof Node && e.target instanceof Node
    && (e.target === swallowTarget || swallowTarget.contains(e.target))
  cancelSwallow()
  if (!samepress) return
  e.stopPropagation()
  e.preventDefault()
}
const cancelSwallow = () => {
  document.removeEventListener('click', swallowNextClick, true)
  swallowTarget = null
  if (swallowTimer !== null) { clearTimeout(swallowTimer); swallowTimer = null }
}

// Capture phase: a stopPropagation() somewhere in the page must not be able to
// strand the tray open.
const onDocumentPointerDown = (e: Event) => {
  const root = rootEl.value
  if (!root) return
  const path = typeof e.composedPath === 'function' ? e.composedPath() : []
  const inside = path.includes(root) || (e.target instanceof Node && root.contains(e.target))
  if (inside) return          // trigger button + tray rows handle themselves
  // A walkthrough card pointing INTO the tray (the Script Show me) is not an
  // outside tap: its Next must not shut the tray it is pointing at, nor have
  // its own click swallowed.
  if (e.target instanceof Element && e.target.closest('[data-walk-card]')) return
  closeTray()
  cancelSwallow()
  swallowTarget = e.target
  document.addEventListener('click', swallowNextClick, true)
  swallowTimer = setTimeout(cancelSwallow, 700)   // no click followed (scroll/drag)
}

const onDocumentKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape') closeTray()
}

watch(isOpen, (open) => {
  if (open) {
    document.addEventListener('pointerdown', onDocumentPointerDown, true)
    document.addEventListener('keydown', onDocumentKeydown)
  } else {
    document.removeEventListener('pointerdown', onDocumentPointerDown, true)
    document.removeEventListener('keydown', onDocumentKeydown)
  }
})

// Tell the walkthrough whether this course has Script mode, so the Script
// Show me opens as an honest "not on this course" on a course without it
// instead of pointing into a list that has no Script row (job #665).
watch(() => props.hasScriptPractice, (has) => { walkCapabilities.set('script', has) }, { immediate: true })

onBeforeUnmount(() => {
  walkCapabilities.delete('script')
  document.removeEventListener('pointerdown', onDocumentPointerDown, true)
  document.removeEventListener('keydown', onDocumentKeydown)
  cancelSwallow()
})

// The trigger is the mixing desk in EVERY mode and one colour in every mode
// (Tom + Aran, 2026-09-26): it is the way into the modes, not a mark of which
// one you are in. Which colour is Aran's call, so on the play copy both are
// live: ?modeicon=white or ?modeicon=green, kept for the tab so in-app
// navigation holds it. Default white.
const MODE_ICON_KEY = 'ssi-play-modeicon'
const modeIconGreen = ((): boolean => {
  try {
    const q = new URLSearchParams(window.location.search).get('modeicon')
    if (q === 'white' || q === 'green') sessionStorage.setItem(MODE_ICON_KEY, q)
    return (q || sessionStorage.getItem(MODE_ICON_KEY)) === 'green'
  } catch {
    return false
  }
})()

// ── The selector (Tom, 2026-09-24): modes are EXCLUSIVE, not on/off switches.
// "only one of them can be selected… the highlighted one is green" — so the
// tray is a list of places, exactly one of them lit, and a tap goes there.
// Offline left the tray for Settings the same day ("a different type of
// thing"), and so did Specialist, which will become its own courses.
//
// The current mode is DERIVED, never stored: the player's own flags say which
// overlay owns the screen (LearningPlayer.setExclusiveMode already keeps them
// exclusive), and off the player RoutedBottomNav says which page we are on via
// pageMode. pageMode '' = a learner page that is none of the modes (/me, the
// inbox), where nothing is lit and Speaking is the way back to the course.
const currentMode = computed<Mode | null>(() => {
  if (props.pageMode !== null) return (props.pageMode || null) as Mode | null
  if (props.isScriptPracticeMode) return 'script'
  if (props.isListeningMode) return 'listening'
  // The pronunciation overlay is not a row; while it is up nothing is lit and
  // Speaking is the way back to the main flow.
  if (props.isPronunciationMode) return null
  return 'speaking'
})

// Tapping the mode you are already in only closes the tray. Tapping another
// hands over to the container, which leaves the old mode on the way (the
// listening and script toggles are the existing exits). Selecting ALWAYS
// closes the tray (Tom 2026-06-11): leaving it open forced a second tap.
const EVENT_FOR: Record<Mode, 'speaking' | 'toggleListening' | 'talk' | 'studyGroups' | 'toggleScriptPractice'> = {
  speaking: 'speaking',
  listening: 'toggleListening',
  talk: 'talk',
  studyGroups: 'studyGroups',
  script: 'toggleScriptPractice',
}
const select = (mode: Mode) => {
  if (currentMode.value !== mode) emit(EVENT_FOR[mode])
  closeTray()
}

// Talk to humans and Study groups (plate A-373; Tom + Aran 2026-09-24) are
// pages, not player overlays, so selecting them navigates. Gated on a
// signed-in, non-guest, non-school learner (showConnect — job #113's gate,
// computed by the container), because everything behind them needs an
// identity and school accounts see neither.
const showTalkRow = computed(() => props.showConnect && !RELEASE_HOLDS.talkToHumans)
const showGroupsRow = computed(() => props.showConnect && !RELEASE_HOLDS.studyGroups)
const studyGroupsDesc = computed(() => {
  const n = myGroupCount.value
  if (!n) return t('modes.studyGroupsAsk')
  if (n === 1) return t('modes.studyGroupsOne')
  return t('modes.studyGroupsCount').replace('{count}', String(n))
})
</script>

<template>
  <div v-show="isVisible" ref="rootEl" class="mode-tray-container">
    <!-- Where am I (job #340): the lit mode's icon, green, top right of the
         screen. Teleported to <body>, so this nav's transform cannot pin it. -->
    <ModeBadge :mode="currentMode" :visible="isVisible" :course-code="courseCode" />
    <!-- Trigger button. The offline-download progress ring is a child of the
         button, anchored to its own box so it stays perfectly concentric. -->
    <button
      class="mode-trigger"
      data-walk="mode-tray"
      :class="{ green: modeIconGreen, open: isOpen }"
      :style="offlineDownloadVisible ? offlineRingVars : undefined"
      @click="toggleTray"
      :title="t('modes.modes')"
    >
      <span
        v-if="offlineDownloadVisible"
        class="mode-dl-ring"
        :class="{ 'is-preparing': offlineDlState === 'preparing' }"
        aria-hidden="true"
      ></span>
      <!-- Sliders icon, always — consistent across every mode (Tom + Aran,
           2026-09-26). The icon used to swap to headphones in Listening mode;
           it no longer does. -->
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/>
        <line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/>
        <line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/>
        <line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/>
        <line x1="17" y1="16" x2="23" y2="16"/>
      </svg>
    </button>

    <!-- Tray popover -->
    <Transition name="tray">
      <div v-if="isOpen" class="mode-tray">
        <!-- The pronunciation (romanisation) guide toggle used to open this
             tray. It moved onto the main-flow player screen on 2026-09-22
             (Tom: "it only applies to main flow") — the guide only renders
             there, so the control lives there too. Do not bring it back.
             Easy / Fast lives on the player's resting screen (the mode you
             pick before you start), not here. Turbo was retired 2026-08-06.
             The Writing on/off row went the same day, same reason — the tray
             had grown too long to read; writing is still switched off by the
             skip-the-writing offer inside Script mode. -->

        <!-- THE SELECTOR (Tom, 2026-09-24): one row per mode, exactly one lit
             green — the mode the learner is in — every other row white. No
             switches: a tap goes to that mode, leaving the one they were in.
             Order is Tom's: Speaking, Listening, Talk to humans, Study groups,
             Script. Offline and Specialist left the tray for Settings. -->

        <!-- Speaking — the main course. Lit whenever no other mode owns the
             screen; tapping it from another mode is the way back. -->
        <button
          class="tray-item tray-item-speaking"
          :class="{ selected: currentMode === 'speaking' }"
          :aria-current="currentMode === 'speaking' ? 'true' : undefined"
          @click="select('speaking')"
        >
          <div class="tray-icon"><ModeIcon mode="speaking" /></div>
          <div class="tray-label">
            <span class="tray-name">{{ t(MODE_NAME_KEY.speaking) }}</span>
            <span class="tray-desc">{{ t('modes.speakingDesc') }}</span>
          </div>
        </button>

        <!-- Listening. Copy comes from settings.listeningMode /
             settings.listeningModeDesc, NOT modes.listening: the settings pair
             is translated in every locale. -->
        <button
          class="tray-item tray-item-listening"
          data-walk="mode-listening"
          :class="{ selected: currentMode === 'listening' }"
          :aria-current="currentMode === 'listening' ? 'true' : undefined"
          @click="select('listening')"
        >
          <div class="tray-icon"><ModeIcon mode="listening" /></div>
          <div class="tray-label">
            <span class="tray-name">{{ t(MODE_NAME_KEY.listening) }}</span>
            <span class="tray-desc">{{ t('settings.listeningModeDesc') }}</span>
          </div>
        </button>

        <!-- Talk to humans (plate A-373). Navigates to /talk; lit while the
             learner is there or on a page behind it. -->
        <button
          v-if="showTalkRow"
          class="tray-item tray-item-talk"
          :class="{ selected: currentMode === 'talk' }"
          :aria-current="currentMode === 'talk' ? 'true' : undefined"
          @click="select('talk')"
        >
          <div class="tray-icon"><ModeIcon mode="talk" /></div>
          <div class="tray-label">
            <span class="tray-name">{{ t(MODE_NAME_KEY.talk) }}</span>
            <span class="tray-desc">{{ t('modes.talkToHumansDesc') }}</span>
          </div>
        </button>

        <!-- Study groups (2026-09-24) — directly below Talk to humans.
             Navigates to /groups; lit while the learner is in the groups. -->
        <button
          v-if="showGroupsRow"
          class="tray-item tray-item-groups"
          :class="{ selected: currentMode === 'studyGroups' }"
          :aria-current="currentMode === 'studyGroups' ? 'true' : undefined"
          @click="select('studyGroups')"
        >
          <div class="tray-icon"><ModeIcon mode="studyGroups" /></div>
          <div class="tray-label">
            <span class="tray-name">{{ t(MODE_NAME_KEY.studyGroups) }}</span>
            <span class="tray-desc">{{ studyGroupsDesc }}</span>
          </div>
        </button>

        <!-- SCRIPT mode — reading and writing the target script, only for
             courses whose script we teach. -->
        <button
          v-if="hasScriptPractice"
          class="tray-item tray-item-script"
          data-walk="mode-script"
          :class="{ selected: currentMode === 'script' }"
          :aria-current="currentMode === 'script' ? 'true' : undefined"
          @click="select('script')"
        >
          <div class="tray-icon"><ModeIcon mode="script" /></div>
          <div class="tray-label">
            <span class="tray-name">{{ t(modeNameKey('script', courseCode)) }}</span>
            <span class="tray-desc">{{ t(isBuildMode(courseCode) ? 'modes.buildDesc' : 'modes.scriptDesc') }}</span>
          </div>
        </button>

      </div>
    </Transition>

    <!-- Dim behind the tray. It is NOT what closes the tray — the document
         pointerdown listener above is (see the note there). This element cannot
         be, and never could be: .bottom-nav's `transform` makes it the
         containing block for `position: fixed`, so `inset: 0` resolves to the
         nav pill, not the viewport. Teleporting it to <body> to escape that is
         the fix that was tried on 2026-07-16 and reverted the same day — in
         embedded play (.player-container.is-teach-embedded, also transformed)
         it re-joins the ROOT stacking context, paints ABOVE the locally
         z-indexed tray and eats every row tap. So it stays local, stays purely
         cosmetic, and the dismiss lives at document level where no containing
         block can shrink it. -->
    <Transition name="backdrop">
      <div v-if="isOpen" class="tray-backdrop" @click="closeTray"></div>
    </Transition>
  </div>
</template>

<style scoped>
.mode-tray-container {
  position: absolute;
  right: 16px;
  bottom: calc(100% + 12px);
  z-index: 103;
}

/* Trigger button + offline-download ring */
/* Offline-download progress ring — a conic-gradient masked into a ring, as a
   CHILD of the button anchored to its own box (inset). It can never drift
   off-centre the way the old overlaid SVG did. --dl-pct (0–100) fills the green
   arc clockwise from 12 o'clock; --dl-color is green (downloading/complete) or
   red (error). complete/error pass pct=100 → full ring. */
.mode-dl-ring {
  position: absolute;
  inset: -5px;                 /* a halo just OUTSIDE the 44px button */
  border-radius: 50%;
  pointer-events: none;
  background: conic-gradient(var(--dl-color, #16a34a) calc(var(--dl-pct, 0) * 1%), rgba(0, 0, 0, 0.13) 0);
  /* cut a 4px ring out of the conic disc (Safari needs -webkit-mask). No
     filter:drop-shadow here — drop-shadow + CSS mask is a known iOS Safari
     rendering quirk; the green + 4px thickness carry the visibility. */
  -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 4px), #000 calc(100% - 4px));
  mask: radial-gradient(farthest-side, transparent calc(100% - 4px), #000 calc(100% - 4px));
  transition: background 0.3s ease;
}
.mode-dl-ring.is-preparing {
  animation: mode-dl-pulse 1s ease-in-out infinite;   /* no total yet → gentle pulse */
}
@keyframes mode-dl-pulse { 0%, 100% { opacity: 0.45; } 50% { opacity: 1; } }
@media (prefers-reduced-motion: reduce) {
  .mode-dl-ring.is-preparing { animation: none; }
}

.mode-trigger {
  position: relative;          /* positioning context for .mode-dl-ring */
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1.5px solid rgba(0, 0, 0, 0.2);
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  color: #6B6560;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.mode-trigger svg {
  width: 20px;
  height: 20px;
}

.mode-trigger:active {
  transform: scale(0.9);
}

/* ?modeicon=green: the old "a mode is on" green, now in every mode. */
.mode-trigger.green {
  background: rgba(240, 255, 245, 0.95);
  border-color: rgba(22, 163, 74, 0.4);
  color: #16a34a;
}

.mode-trigger.open {
  background: rgba(0, 0, 0, 0.08);
  border-color: rgba(0, 0, 0, 0.35);
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.15),
    0 0 0 3px rgba(0, 0, 0, 0.06);
}

/* Tray popover */
.mode-tray {
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
  width: 280px;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  border: 1.5px solid rgba(0, 0, 0, 0.12);
  border-radius: 16px;
  padding: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15), 0 12px 32px rgba(0, 0, 0, 0.1);
  z-index: 103;
}

/* Tray items */
.tray-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  background: transparent;
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.15s ease;
  text-align: left;
  color: #6B6560;
}

.tray-item:hover {
  background: rgba(0, 0, 0, 0.04);
}

.tray-item:active {
  background: rgba(0, 0, 0, 0.08);
}

/* Rows are spaced apart so each reads as its own choice, like the options
   of a segmented control rather than a settings list. */
.tray-item + .tray-item {
  margin-top: 4px;
}

/* The one selected mode: filled green with white text, the same #16a34a that
   lit the old "on" switches. Every other row stays white. */
.tray-item.selected,
.tray-item.selected:hover,
.tray-item.selected:active {
  background: #16a34a;
  color: #fff;
}

.tray-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.04);
  flex-shrink: 0;
}

.tray-item.selected .tray-icon {
  background: rgba(255, 255, 255, 0.2);
}

.tray-item.selected .tray-desc {
  color: rgba(255, 255, 255, 0.85);
}

.tray-icon svg {
  width: 18px;
  height: 18px;
}

.tray-label {
  flex: 1;
  min-width: 0;
}

.tray-name {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: inherit;
  line-height: 1.2;
}

.tray-desc {
  display: block;
  font-size: 11px;
  color: #A09A94;
  line-height: 1.3;
  margin-top: 1px;
}

/* Backdrop */
.tray-backdrop {
  position: fixed;
  inset: 0;
  z-index: 102;
  background: rgba(0, 0, 0, 0.15);
}

/* Transitions */
.tray-enter-active,
.tray-leave-active {
  transition: all 0.2s ease;
}

.tray-enter-from,
.tray-leave-to {
  opacity: 0;
  transform: translateY(8px) scale(0.95);
}

.backdrop-enter-active,
.backdrop-leave-active {
  transition: opacity 0.2s ease;
}

.backdrop-enter-from,
.backdrop-leave-to {
  opacity: 0;
}
</style>
